import { ApolloClient, createHttpLink, from, InMemoryCache, split, TypePolicies } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { getMainDefinition } from '@apollo/client/utilities'
import { Kind, OperationTypeNode } from 'graphql'

import { getAPIConfig } from '@/utils/apiConfig'
import { getAccessToken, getNewToken } from '@/utils/auth'
import { buildCompositeKey } from '@/utils/format/key'

import { AppSyncWebSocketLink } from './appSyncWebSocket'

const httpLink = createHttpLink()

export const wsLink = new AppSyncWebSocketLink()

const authLink = setContext(async (_, { headers }) => {
  const token = await getAccessToken()
  const apiConfig = await getAPIConfig()

  if (!token) {
    window.location.href = '/sign-out'
  }

  return {
    uri: apiConfig.graphql,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    headers: {
      ...headers,
      authorization: token,
    },
  }
})

export const generateErrorLink = ({
  refreshTokenRequestFunc,
  appsyncWebSocket,
}: {
  refreshTokenRequestFunc: () => Promise<void>
  appsyncWebSocket: AppSyncWebSocketLink
}) => {
  return [
    onError(({ graphQLErrors, networkError, operation, forward }) => {
      if (!graphQLErrors) return
      if (networkError) console.error(`[Network error]: ${networkError}`)

      for (const { message } of graphQLErrors) {
        console.error(`[GraphQL error]: Message: ${message}`)
        if (message !== 'Unauthorized') continue

        const { getContext, setContext } = operation
        const context = getContext()

        setContext({
          ...context,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          headers: {
            ...context?.headers,
            _needsRefresh: true,
          },
        })

        return forward(operation)
      }
    }),
    setContext(async (_, previousContext) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (previousContext?.headers?._needsRefresh) {
        await refreshTokenRequestFunc()
        await appsyncWebSocket.refreshToken()
      }
      return previousContext
    }),
  ]
}

const __typenames = [
  'Shelf',
  'Account',
  'Author',
  'Collection',
  'Title',
  'Item',
  'Library',
  'Organization',
  'Notification',
  'Permission',
  'Member',
  'LoanTransaction',
  'MemberDevice',
  'RecordLog',
  'Role',
  'Tag',
  'RoleOfMember',
  'UserPreference',
]

const typePolicies: TypePolicies = __typenames.reduce(
  (a, name) => ({
    ...a,
    [name]: {
      keyFields: ['Id', 'ShardId'],
      fields: {
        __key: {
          read(_: unknown, { readField }: unknown) {
            /* eslint-disable @typescript-eslint/no-unsafe-call */
            const id = readField('Id') as string
            const shardId = readField('ShardId') as string
            return buildCompositeKey(shardId, id)
          },
        },
      },
    },
  }),
  {},
)

const link = from([
  ...generateErrorLink({
    refreshTokenRequestFunc: getNewToken,
    appsyncWebSocket: wsLink,
  }),
  authLink,
  httpLink,
])

const realtimeLink = from([
  ...generateErrorLink({
    refreshTokenRequestFunc: getNewToken,
    appsyncWebSocket: wsLink,
  }),
  wsLink,
])

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.SUBSCRIPTION
  },
  realtimeLink,
  link,
)

export const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies,
  }),
})
